import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Dice5, Home, Coins, TrendingUp, Wallet, Search, Trophy, Check, X as XIcon } from 'lucide-react';
import { useAuth, formatApiError } from '../../context/AuthContext';
import WalletButton from './WalletButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MARKET_ICONS = {
  artilheiro: '⚽',
  assistencias: '🎯',
  player_goals_over: '📈',
  total_goals_over: '📊',
};

const StatusPill = ({ status }) => {
  const map = {
    open: { c: 'bg-sky-500/20 text-sky-300 border-sky-400/30', l: 'Aberta' },
    won: { c: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30', l: 'Ganha' },
    lost: { c: 'bg-red-500/20 text-red-300 border-red-400/30', l: 'Perdida' },
    refunded: { c: 'bg-amber-500/20 text-amber-300 border-amber-400/30', l: 'Reembolsada' },
  };
  const m = map[status] || map.open;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${m.c}`}>{m.l}</span>;
};

const BetsPage = () => {
  const { user, refreshMe } = useAuth();
  const [data, setData] = useState(null);
  const [mine, setMine] = useState([]);
  const [activeMarket, setActiveMarket] = useState('artilheiro');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null); // { market, selection, label }
  const [amount, setAmount] = useState(100);
  const [placing, setPlacing] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');

  const load = useCallback(async () => {
    setErr('');
    try {
      const [m, mm] = await Promise.all([
        axios.get(`${API}/fantasy/bets/markets`),
        axios.get(`${API}/fantasy/bets/mine`),
      ]);
      setData(m.data);
      setMine(mm.data);
    } catch (ex) { setErr(formatApiError(ex)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const market = useMemo(() => (data?.markets || []).find((m) => m.market === activeMarket), [data, activeMarket]);

  const filteredOptions = useMemo(() => {
    if (!market) return [];
    const q = search.trim().toLowerCase();
    if (!q) return market.options;
    return market.options.filter((o) => (o.label || '').toLowerCase().includes(q));
  }, [market, search]);

  const place = async () => {
    if (!selected) return;
    setPlacing(true); setErr(''); setOk('');
    try {
      const body = {
        match_day_id: data?.match_day?.id,
        market: selected.market,
        selection: selected.selection,
        amount: Number(amount),
      };
      const { data: res } = await axios.post(`${API}/fantasy/bets/place`, body);
      setOk(`Aposta registada! Saldo actual: ${res.new_balance} PC`);
      setSelected(null); setAmount(100);
      await refreshMe();
      await load();
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setPlacing(false); }
  };

  const balance = user?.procoins_balance ?? 0;
  const marketOpen = data?.market_open;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-fuchsia-950/40 text-white">
      <header className="border-b border-white/10 backdrop-blur-md sticky top-0 z-40 bg-slate-950/60">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-fuchsia-500 flex items-center justify-center shrink-0"><Dice5 className="w-6 h-6 text-slate-900" /></div>
            <div className="min-w-0">
              <h1 className="text-lg font-black leading-tight">Apostas da Pelada</h1>
              <p className="text-xs text-slate-400 truncate">Parimutuel · 15% rake · 85% aos vencedores</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <WalletButton balance={balance} />
            <Link to="/fantasy" className="text-slate-300 hover:text-white text-sm inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/5" data-testid="bets-home-link">
              <Home className="w-4 h-4" /> <span className="hidden sm:inline">Fantasy</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {err && <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{err}</div>}
        {ok && <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">{ok}</div>}

        {!data ? (
          <p className="text-slate-400 text-center py-12">A carregar mercados...</p>
        ) : !data.match_day ? (
          <p className="text-slate-400 text-center py-12">Sem jornada activa. Volta quando o admin abrir a próxima.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {(data.markets || []).map((m) => (
                <button
                  key={m.market}
                  onClick={() => { setActiveMarket(m.market); setSelected(null); setSearch(''); }}
                  className={`text-left rounded-xl border p-3 transition-colors ${activeMarket === m.market ? 'bg-fuchsia-500/20 border-fuchsia-400/60 ring-1 ring-fuchsia-400/40' : 'bg-slate-900/60 border-white/10 hover:bg-slate-800/60'}`}
                  data-testid={`bets-market-${m.market}`}
                >
                  <div className="text-2xl">{MARKET_ICONS[m.market] || '🎲'}</div>
                  <div className="text-sm font-bold mt-1">{m.label}</div>
                  <div className="text-[11px] text-slate-400">Pote: <span className="text-emerald-300 tabular-nums">{m.total_pot} PC</span></div>
                </button>
              ))}
            </div>

            <section className="bg-slate-900/60 border border-white/10 rounded-2xl p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="text-white font-black text-base">{market?.label}</h2>
                  <p className="text-xs text-slate-400">Pote total: <span className="text-emerald-300 font-bold tabular-nums">{market?.total_pot ?? 0} PC</span> · Vencedores partilham <span className="text-fuchsia-300 font-bold tabular-nums">{market?.winners_pool ?? 0} PC</span> (85%)</p>
                </div>
                {(market?.kind === 'player' || market?.kind === 'player_ou') && (
                  <div className="relative">
                    <Search className="w-4 h-4 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filtrar jogador..."
                      className="bg-slate-800/60 border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
                      data-testid="bets-search-input" />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-4 max-h-[420px] overflow-y-auto pr-1">
                {filteredOptions.map((o) => {
                  const isSel = selected?.market === activeMarket && selected?.selectionKey === o.key;
                  return (
                    <button
                      key={o.key}
                      onClick={() => {
                        const sel = { market: activeMarket, selectionKey: o.key, label: o.label };
                        if (market.kind === 'player') sel.selection = { player_name: o.player_name };
                        else if (market.kind === 'player_ou') sel.selection = { player_name: o.player_name, over: o.over };
                        else sel.selection = { over: o.over };
                        setSelected(sel);
                      }}
                      className={`text-left rounded-lg border p-3 transition-colors ${isSel ? 'bg-fuchsia-500/25 border-fuchsia-400/60' : 'bg-slate-800/50 border-white/10 hover:bg-slate-700/60'}`}
                      data-testid={`bets-option-${o.key}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-white font-semibold text-sm truncate">{o.label}</div>
                        <div className="text-fuchsia-300 font-black tabular-nums text-xs">×{o.multiplier > 0 ? o.multiplier : '–'}</div>
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-2">
                        <span className="tabular-nums">Pote: {o.total_stake} PC</span>
                        <span>·</span>
                        <span>{o.count} {o.count === 1 ? 'aposta' : 'apostas'}</span>
                      </div>
                    </button>
                  );
                })}
                {filteredOptions.length === 0 && <p className="text-slate-500 text-sm col-span-full text-center py-4">Sem resultados.</p>}
              </div>
            </section>

            <section className="bg-slate-900/70 border border-fuchsia-500/30 rounded-2xl p-4">
              <h3 className="text-white font-black text-sm mb-3 flex items-center gap-2"><Coins className="w-4 h-4 text-fuchsia-300" /> Fazer aposta</h3>
              {!selected ? (
                <p className="text-sm text-slate-400">Escolhe uma opção acima para apostar.</p>
              ) : (
                <div className="grid md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <div className="text-xs text-slate-400 uppercase">Aposta selecionada</div>
                    <div className="text-lg font-black text-fuchsia-200 mt-0.5">{selected.label}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{market?.label}</div>
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 uppercase">Quantia (PC)</label>
                    <div className="flex items-center gap-2 mt-1">
                      <input type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)}
                        className="w-full bg-slate-800/70 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-500 tabular-nums"
                        data-testid="bets-amount-input" />
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1"><Wallet className="w-3 h-3" /> Tens {balance} PC disponíveis</div>
                  </div>
                  <div className="md:col-span-3 flex flex-wrap gap-2">
                    {[50, 100, 250, 500].map((v) => (
                      <button key={v} type="button" onClick={() => setAmount(v)}
                        className="text-xs bg-slate-800/70 border border-white/10 rounded-full px-3 py-1 hover:bg-slate-700/70">
                        +{v}
                      </button>
                    ))}
                    <button
                      disabled={!marketOpen || placing || Number(amount) <= 0 || Number(amount) > balance}
                      onClick={place}
                      className="ml-auto bg-fuchsia-500 hover:bg-fuchsia-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 font-black rounded-lg px-4 py-2 text-sm flex items-center gap-2"
                      data-testid="bets-place-btn">
                      <TrendingUp className="w-4 h-4" /> {placing ? 'A registar...' : 'Apostar'}
                    </button>
                  </div>
                  {!marketOpen && <p className="md:col-span-3 text-amber-300 text-xs bg-amber-500/10 border border-amber-400/30 rounded-lg p-2">Mercado fechado — apostas reabrem na próxima jornada.</p>}
                </div>
              )}
            </section>

            <section>
              <h3 className="text-white font-black text-sm mb-3 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-300" /> As minhas apostas</h3>
              {mine.length === 0 ? (
                <p className="text-slate-400 text-sm">Ainda não tens apostas. Faz a tua primeira ali em cima!</p>
              ) : (
                <div className="grid gap-2">
                  {mine.map((b) => (
                    <div key={b.id} className="bg-slate-900/60 border border-white/10 rounded-xl p-3 flex items-center justify-between gap-3" data-testid={`my-bet-${b.id}`}>
                      <div className="min-w-0">
                        <div className="text-[11px] text-slate-400 uppercase tracking-wide">{b.market_label}</div>
                        <div className="text-white font-bold text-sm truncate">{b.selection_label}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <StatusPill status={b.status} />
                        <div className="mt-1 text-xs text-slate-400">
                          <span className="text-slate-300 tabular-nums">-{b.amount} PC</span>
                          {b.status === 'won' && <span className="ml-2 text-emerald-300 font-bold tabular-nums">+{b.payout} PC</span>}
                          {b.status === 'refunded' && <span className="ml-2 text-amber-300 font-bold tabular-nums">+{b.payout} PC</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default BetsPage;
